import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Btn from '../../../../../components/Buttons/Btn';
import Input from '../../../../../components/Input';
import Modal from '../../../../../components/Modal';
import SelectAsyncJSX from '../../../../../components/SelectAsync';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../components/Notification';
import HttpStatus from '../../../../../enums/httpStatus';
import { superContactsReq, tenantsReq } from '../../../../../services/requests';
import { ContactOtherInfo } from '../../../../../types/apiResponse/contact';
import { SelectOption } from '../../../../../utils/getSelectOptions';
import OtherInfoModal from '../OtherInfoModal';

type Props = {
  showModal: boolean;
  closeModal: () => void;
  callback: () => void;
};

type TenantOptionItem = {
  id: number;
  name: string;
};

const AddContactModal = ({ showModal, closeModal, callback }: Props) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [tenantSelected, setTenantSelected] = useState<SelectOption | null>(null);
  const [contact, setContact] = useState('');
  const [sector, setSector] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otherInfo, setOtherInfo] = useState<ContactOtherInfo>({});
  const [showOtherInfoModal, setShowOtherInfoModal] = useState(false);

  const resetForm = () => {
    setTenantSelected(null);
    setContact('');
    setSector('');
    setEmail('');
    setPhone('');
    setOtherInfo({});
  };

  const handleClose = () => {
    resetForm();
    closeModal();
  };

  const validate = () => {
    if (!tenantSelected?.value) {
      frontendNotification({
        message: t('tenantIsRequired'),
        type: 'warning',
      });

      return false;
    }

    if (contact.trim().length === 0) {
      frontendNotification({
        message: t('contactIsRequired'),
        type: 'warning',
      });

      return false;
    }

    if (sector.trim().length === 0) {
      frontendNotification({
        message: t('sectorIsRequired'),
        type: 'warning',
      });

      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setIsLoading(true);
    try {
      superContactsReq.query = '';
      const response = await superContactsReq.store({
        tenant_id: Number(tenantSelected?.value),
        contact: contact.trim(),
        sector: sector.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        other_info: Object.keys(otherInfo).length > 0 ? otherInfo : null,
      });

      if (response.status !== HttpStatus.CREATED) {
        throw response;
      }

      frontendNotification({
        message: t('successfullyCreated'),
        type: 'success',
      });

      callback();
      handleClose();
    } catch (err) {
      if (!superContactsReq.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    setIsLoading(false);
  };

  return (
    <>
      <Modal
        title={t('contacts')}
        showModal={showModal}
        closeModal={handleClose}
        cancelButton={handleClose}
        action={() => handleSave()}
        isLoading={isLoading}
        bodyStyle={{ overflow: 'visible' }}
      >
        <div className="row">
          <div className="inputWithLabel" style={{ minWidth: '260px' }}>
            <div className="label">{t('tenant') as string}</div>
            <SelectAsyncJSX
              value={tenantSelected}
              onChange={setTenantSelected}
              request={tenantsReq}
              reqResponseToOption={{
                mapper: {
                  label: (item: TenantOptionItem) => `${item.id} - ${item.name}`,
                  value: (item: TenantOptionItem) => item.id,
                },
              }}
              placeholder={t('select')}
              disabled={isLoading}
              isRequired
              query="quantityPerPage=50"
            />
          </div>

          <div className="inputWithLabel">
            <Input
              label={t('Nome')}
              value={contact}
              onChange={(event) => setContact(event.target.value)}
              required={t('contactIsRequired')}
              isRequired
              disabled={isLoading}
            />
          </div>

          <div className="inputWithLabel">
            <Input
              label={t('sector')}
              value={sector}
              onChange={(event) => setSector(event.target.value)}
              required={t('sectorIsRequired')}
              isRequired
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="row">
          <div className="inputWithLabel">
            <Input
              label={t('email')}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="inputWithLabel">
            <Input
              label={t('phone')}
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="row" style={{ alignItems: 'flex-end', gap: '12px' }}>
          <Btn
            text={t('manageOtherInfo')}
            onClick={() => setShowOtherInfoModal(true)}
            disabled={isLoading}
            style={{ maxWidth: '260px' }}
          />
          <div style={{ paddingBottom: '8px' }}>
            {Object.keys(otherInfo).length > 0
              ? `${Object.keys(otherInfo).length} ${t('items')}`
              : t('noOtherInfo')}
          </div>
        </div>
      </Modal>

      {showOtherInfoModal && (
        <OtherInfoModal
          showModal={showOtherInfoModal}
          closeModal={() => setShowOtherInfoModal(false)}
          value={otherInfo}
          onSave={(value) => setOtherInfo(value)}
        />
      )}
    </>
  );
};

export default AddContactModal;
